import React, { createContext, useCallback, useContext, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

// Lets a page render its own header content (title, actions, context controls,
// filters) into named regions of the global TopBar, so per-page controls live
// in the single 3-row app header. Also carries the current record name so a
// detail page's breadcrumb tail can show it (page → header, effect-based).
const HeaderSlotContext = createContext({
	nodes: {},
	setSlotNode: () => {},
	recordName: null,
	setRecordName: () => {},
	occupancy: {},
	bumpOccupancy: () => {},
});

export function HeaderSlotProvider({ children }) {
	const [nodes, setNodes] = useState({});
	const [recordName, setRecordName] = useState(null);
	// occupancy — how many HeaderPortals are currently rendering into each slot.
	// Lets the TopBar know whether a page injected its own title (suppress the
	// derived fallback title) without inspecting portal DOM. Ref-counted so
	// remounts during a route transition don't prematurely read as empty.
	const [occupancy, setOccupancy] = useState({});

	const setSlotNode = useCallback((name, node) => {
		setNodes((prev) => {
			if (prev[name] === node) return prev;
			const next = { ...prev };
			if (node) next[name] = node;
			else delete next[name];
			return next;
		});
	}, []);

	const bumpOccupancy = useCallback((name, delta) => {
		setOccupancy((prev) => {
			const count = (prev[name] || 0) + delta;
			const next = { ...prev };
			if (count > 0) next[name] = count;
			else delete next[name];
			return next;
		});
	}, []);

	return (
		<HeaderSlotContext.Provider value={{ nodes, setSlotNode, recordName, setRecordName, occupancy, bumpOccupancy }}>
			{children}
		</HeaderSlotContext.Provider>
	);
}

// Ref callback for a named TopBar slot element. Defaults to "actions" so the
// legacy no-arg call keeps registering the single actions slot.
export function useHeaderSlotRef(name = "actions") {
	const { setSlotNode } = useContext(HeaderSlotContext);
	return useCallback((node) => setSlotNode(name, node), [setSlotNode, name]);
}

export function useHeaderSlotNode(name) {
	return useContext(HeaderSlotContext).nodes[name] || null;
}

// Render children into a named TopBar slot. Falls back to inline when that slot
// isn't mounted (unit tests, or before the TopBar mounts). Default slot keeps
// existing <HeaderPortal> callers pointing at the actions region. Registers the
// slot as occupied for its lifetime so the TopBar can react to what a page
// actually injected (e.g. suppress the derived title when a page sets its own).
export function HeaderPortal({ slot = "actions", children }) {
	const { bumpOccupancy } = useContext(HeaderSlotContext);
	const node = useHeaderSlotNode(slot);
	// useLayoutEffect (not useEffect) so occupancy resolves synchronously before
	// paint — otherwise TopBar shows a derived title / hides row 2 for one frame
	// before the page's portal registers, causing a visible flash.
	useLayoutEffect(() => {
		bumpOccupancy(slot, 1);
		return () => bumpOccupancy(slot, -1);
	}, [slot, bumpOccupancy]);
	return node ? createPortal(children, node) : <>{children}</>;
}

// True while at least one HeaderPortal is rendering into the named slot.
export function useHeaderSlotOccupied(name) {
	return (useContext(HeaderSlotContext).occupancy[name] || 0) > 0;
}

// Page-side: declare the record name for the breadcrumb tail. Sets on mount,
// clears on unmount so it never leaks to the next route.
export function useSetHeaderRecordName(name) {
	const { setRecordName } = useContext(HeaderSlotContext);
	useLayoutEffect(() => {
		setRecordName(name ?? null);
		return () => setRecordName(null);
	}, [name, setRecordName]);
}

export function useHeaderRecordName() {
	return useContext(HeaderSlotContext).recordName;
}
